import { create } from "zustand";
import { createApi } from "../../../../utils/createApi";
import { shouldOfferRebootToApply } from "../../../../utils/shouldOfferRebootToApply";
import { waitForSettingsDeployed } from "../../SiteAboutPage/SiteAboutPageUtils/waitForSettingsDeployed";

/**
 * Restart prompt after a bulk SYNC from the Sites list (ENG-2800).
 *
 * Sync deploys settings only; a running connector keeps its previous settings
 * until it restarts. The single-site Sync flows already ask "reboot now to
 * apply?". Bulk SYNC asked nothing, which only worked because admin's Sync
 * used to restart running sites itself. This gives bulk SYNC the same
 * explicit choice: after the deploys, find the synced sites that are armed
 * AND meant to be armed now (shouldOfferRebootToApply, ENG-811) and offer to
 * restart those. Flex-schedule sites can't be rebooted site-wide (ENG-389),
 * so they are listed for the About page's per-schedule picker instead.
 * deploy_next only queues the deploy, so a site is offered a restart only
 * after its settings_deployed confirms; restarting earlier would boot the
 * connector on the old settings.
 */

export type BulkRestartSite = { id: string; name: string };

export type BulkRestartCandidates = {
  restart: BulkRestartSite[];
  flex: BulkRestartSite[];
  /** Deploy not confirmed within the wait; state unknown, never restarted. */
  unconfirmed: BulkRestartSite[];
};

type AboutSnapshot = {
  name?: string;
  armed?: boolean;
  arm_intended?: boolean;
  has_flex_schedule?: boolean;
};

/** Sort one site's fresh /about/ into restart, flex, or neither. */
export const classifyRestartCandidate = (
  about: AboutSnapshot
): keyof BulkRestartCandidates | null => {
  if (!shouldOfferRebootToApply(about)) return null;
  return about.has_flex_schedule ? "flex" : "restart";
};

/**
 * Decide from FRESH site state (never the list rows, which carry no
 * arm_intended and may predate the sync), read once the site's deploy has
 * confirmed. A site whose deploy doesn't confirm in time is reported as
 * unconfirmed rather than restarted.
 */
export const fetchRestartCandidates = async (
  siteIds: readonly string[],
  namesById: Record<string, string>
): Promise<BulkRestartCandidates> => {
  const AxiosUI = createApi(``);
  const results = await Promise.all(
    siteIds.map((id) => waitForSettingsDeployed(AxiosUI, id))
  );
  const candidates: BulkRestartCandidates = {
    restart: [],
    flex: [],
    unconfirmed: [],
  };
  results.forEach((result, index) => {
    const id = siteIds[index];
    const about: AboutSnapshot = result.siteInfo ?? {};
    const site = { id, name: about.name || namesById[id] || id };
    if (!result.deployed) {
      candidates.unconfirmed.push(site);
      return;
    }
    const bucket = classifyRestartCandidate(about);
    if (bucket) candidates[bucket].push(site);
  });
  return candidates;
};

/** Restart each site; returns the names of the ones that failed. */
export const restartSites = async (
  sites: readonly BulkRestartSite[]
): Promise<string[]> => {
  const AxiosUI = createApi(``);
  // action/reboot/ logs a "Manual Reboot", not a "Manual Arm" (ENG-1380).
  const results = await Promise.allSettled(
    sites.map((site) => AxiosUI.post(`customer/${site.id}/action/reboot/`))
  );
  return sites
    .filter((_, index) => results[index].status === "rejected")
    .map((site) => site.name);
};

type BulkRestartState = BulkRestartCandidates & {
  open: boolean;
  openPrompt: (candidates: BulkRestartCandidates) => void;
  close: () => void;
};

const EMPTY: BulkRestartCandidates = { restart: [], flex: [], unconfirmed: [] };

export const useBulkRestartStore = create<BulkRestartState>((set) => ({
  ...EMPTY,
  open: false,
  openPrompt: (candidates) => {
    if (
      candidates.restart.length === 0 &&
      candidates.flex.length === 0 &&
      candidates.unconfirmed.length === 0
    ) {
      return;
    }
    set({ ...candidates, open: true });
  },
  close: () => set({ ...EMPTY, open: false }),
}));
